import { Injectable, inject } from '@angular/core';
import { LoggedUserService } from '@keus-template/auth';
import { environment } from '../environments/environment';

declare global {
  interface Window {
    Intercom: any,
  }
}

@Injectable({
  providedIn: 'root'
})
export class IntercomService {
  private readonly _loggedUserService = inject(LoggedUserService);
  private isInitialized = false;
  private isScriptLoaded = false;
  private initializationPromise: Promise<void> | null = null;

  /**
   * Initialize Intercom with user data and settings
   * This should be called after user login
   */
  public initializeIntercom(appId: string): Promise<void> {
    // Return existing initialization if in progress
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // Already initialized
    if (this.isInitialized) {
      return Promise.resolve();
    }

    this.initializationPromise = this.performInitialization(appId);
    return this.initializationPromise;
  }

  private async performInitialization(appId: string): Promise<void> {
    try {
      // Load Intercom script if not already loaded
      if (!this.isScriptLoaded) {
        await this.loadIntercomScript(appId);
        this.isScriptLoaded = true;
      }
      
      // Get current user data
      const user = this._loggedUserService.currentUser();
      
      if (user) {
        
        // Boot Intercom with user data
        window.Intercom('boot', {
          app_id: appId,
          user_id: user.id,
          email: user.email,
          name: `${user.firstName} ${user.lastName}`,
          created_at: Math.floor(new Date(user.createdAt || Date.now()).getTime() / 1000),
          // Custom attributes
          custom_attributes: {
            role: user.userType || user.role,
            tenant: environment.tenant,
            taxCode: user.taxCode,
            phone: user.phone,
            region: user.region,
            profilePic: user.profilePic
          },
          // Hide the default launcher
          hide_default_launcher: true
        });
        
        this.isInitialized = true;
      } else {
        console.warn('[IntercomService] No user data available for Intercom initialization');
      }
    } catch (error) {
      console.error('[IntercomService] Failed to initialize Intercom:', error);
      this.initializationPromise = null;
      throw error;
    }
  }

  /**
   * Load Intercom script dynamically
   */
  private loadIntercomScript(appId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script already exists
      if (document.querySelector(`script[src*="widget.intercom.io"]`)) {
        resolve();
        return;
      }

      // Initialize Intercom global function
      window.Intercom = window.Intercom || function() {
        (window.Intercom.q = window.Intercom.q || []).push(arguments);
      };
      window.Intercom.q = window.Intercom.q || [];

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `https://widget.intercom.io/widget/${appId}`;
      
      script.onload = () => {
        resolve();
      };
      
      script.onerror = (error) => {
        console.error('[IntercomService] Failed to load Intercom script:', error);
        reject(error);
      };
      
      const firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode?.insertBefore(script, firstScript);
    });
  }

  /**
   * Show Intercom messenger programmatically
   */
  public async showMessenger(): Promise<void> {
    // Check if we're using a test App ID
    if (environment.intercomAppId?.startsWith('test_app_id')) {
      console.warn('[IntercomService] Cannot show Intercom - using test App ID. Please configure a real Intercom App ID in environment files.');
      alert('Intercom is not configured. Please add your Intercom App ID to the environment configuration.');
      return;
    }

    // If not initialized, try to initialize first
    if (!this.isInitialized && environment.intercomAppId) {
      await this.initializeIntercom(environment.intercomAppId);
    }

    if (window.Intercom && this.isInitialized) {
      window.Intercom('show');
    } else {
      console.warn('[IntercomService] Cannot show Intercom - not initialized or no app ID configured');
    }
  }

  /**
   * Hide Intercom messenger
   */
  public hideMessenger(): void {
    if (window.Intercom) {
      window.Intercom('hide');
    }
  }

  /**
   * Update user data in Intercom
   */
  public updateUser(userData: any): void {
    if (window.Intercom) {
      window.Intercom('update', userData);
    }
  }

  /**
   * Shutdown Intercom (on logout)
   */
  public shutdown(): void {
    if (window.Intercom) {
      window.Intercom('shutdown');
      this.isInitialized = false;
    }
  }

  /**
   * Track custom events
   */
  public trackEvent(eventName: string, metadata?: any): void {
    if (window.Intercom) {
      window.Intercom('trackEvent', eventName, metadata);
    }
  }

  /**
   * Show new message with pre-filled text
   */
  public showNewMessage(prefilledMessage?: string): void {
    if (window.Intercom) {
      if (prefilledMessage) {
        window.Intercom('showNewMessage', prefilledMessage);
      } else {
        window.Intercom('showNewMessage');
      }
    }
  }
}